import { useT } from "../lib/i18n.jsx";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api.js";
import { IconCheck, IconX, IconShield } from "../components/icons.jsx";

const wrapStyle = {
  minHeight: "100vh",
  background: "var(--bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
};

const cardStyle = {
  width: "100%",
  maxWidth: "460px",
  background: "var(--bg-elev)",
  border: "1px solid var(--line-strong)",
  borderRadius: "var(--r-lg)",
  padding: "36px",
  position: "relative",
  overflow: "hidden",
};

const cardAccent = {
  position: "absolute",
  top: 0, left: 0, right: 0,
  height: "2px",
  background: "linear-gradient(90deg, transparent, var(--acc), transparent)",
};

export default function SsoAuthorizePage() {
  const { t } = useT();
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const port = searchParams.get("port");

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api(`/sso/info/${encodeURIComponent(code)}`)
      .then(setInfo)
      .catch((err) => setError(err.message));
  }, [code]);

  async function approve() {
    setBusy(true);
    try {
      await api(`/sso/approve/${encodeURIComponent(code)}`, { method: "POST" });
      setDone("approved");
      if (port) {
        const url = `http://127.0.0.1:${port}/cb?code=${encodeURIComponent(code)}`;
        setTimeout(() => { window.location.href = url; }, 800);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function deny() {
    setBusy(true);
    try {
      await api(`/sso/deny/${encodeURIComponent(code)}`, { method: "POST" });
      setDone("denied");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={wrapStyle}>
        <motion.div
          style={cardStyle}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={cardAccent} />
          <div className="eyebrow">{done === "approved" ? "Approved" : "Denied"}</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: "8px 0 6px" }}>
            {done === "approved" ? "All set" : "Connection denied"}
          </h1>
          <p style={{ fontSize: 13, color: "var(--text-dim)", lineHeight: 1.55 }}>
            {done === "approved"
              ? "You can close this tab and return to Vorks Connect — it will pick up the session automatically."
              : "The Vorks Connect client will not get access to your account."}
          </p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={wrapStyle}>
        <div style={cardStyle}>
          <div style={cardAccent} />
          <div className="eyebrow">Error</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: "8px 0 6px" }}>Authorization failed</h1>
          <div className="error-line" style={{ marginTop: 16 }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div style={wrapStyle}>
        <div className="loading">Checking code…</div>
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <motion.div
        style={cardStyle}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div style={cardAccent} />

        <div style={{
          width: 48, height: 48,
          borderRadius: "50%",
          border: "1px solid var(--acc)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--acc)",
          marginBottom: 20,
          boxShadow: "0 0 20px var(--acc-glow)",
        }}>
          <IconShield size={22} />
        </div>

        <div className="eyebrow">Device authorization</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: "8px 0 6px" }}>
          Sign in to Vorks Connect
        </h1>
        <p style={{ fontSize: 13, color: "var(--text-dim)", lineHeight: 1.55, margin: "0 0 20px" }}>
          A new device is requesting access to your account. Approve only if you
          started this from a trusted device.
        </p>

        <div style={{
          background: "var(--bg)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r)",
          padding: "14px 16px",
          marginBottom: 20,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "8px 0", borderBottom: "1px solid var(--line)",
          }}>
            <div style={{
              fontFamily: "var(--mono)", fontSize: 10,
              color: "var(--text-dim)", letterSpacing: "0.1em", textTransform: "uppercase",
            }}>Device</div>
            <div className="mono" style={{ fontSize: 13 }}>
              {info.device_name || "Unnamed device"}
            </div>
          </div>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "8px 0",
          }}>
            <div style={{
              fontFamily: "var(--mono)", fontSize: 10,
              color: "var(--text-dim)", letterSpacing: "0.1em", textTransform: "uppercase",
            }}>From IP</div>
            <div className="mono" style={{ fontSize: 13 }}>
              {info.client_ip || "—"}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn ghost danger" onClick={deny} disabled={busy} style={{ flex: 1 }}>
            <IconX size={12} /> Deny
          </button>
          <button className="btn primary" onClick={approve} disabled={busy} style={{ flex: 2 }}>
            <IconCheck size={12} /> {busy ? "Approving…" : "Allow this device"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
